import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IconButton, CircularProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useTranslation } from 'react-i18next';
import { fetchVocabAudioBlob } from '../../services/vocabAudio.service';

interface WordAudioButtonProps {
  word: string;
  language: string;
  sx?: SxProps<Theme>;
}

type Status = 'idle' | 'loading' | 'error';

const SYNTH_TIMEOUT_MS = 15_000;

/**
 * Module-level cache of object URLs keyed by `language|word`.
 *
 * The blob-fetch approach loses the browser's HTTP cache that the spec relied on
 * for repeat plays, so we preserve the "no dedupe, but cheap repeats" intent by
 * caching the object URL here. On a cache hit we skip the network entirely.
 *
 * Tradeoff: entries are intentionally never revoked (other mounted buttons may
 * share them), so this cache leaks a bounded number of small object URLs for the
 * lifetime of the page. Acceptable given vocab review sessions touch a limited
 * set of words. Per-instance object URLs that are NOT promoted to this cache are
 * still revoked on unmount.
 */
const audioUrlCache = new Map<string, string>();

export const WordAudioButton = forwardRef<HTMLButtonElement, WordAudioButtonProps>(function WordAudioButton({ word, language, sx }, forwardedRef) {
  const { t } = useTranslation('vocabulary');
  const [status, setStatus] = useState<Status>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  useImperativeHandle(forwardedRef, () => buttonRef.current as HTMLButtonElement);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const synthTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  // Object URLs this component instance created that are NOT in the module cache.
  const ownedUrls = useRef<Set<string>>(new Set());

  const clearErrorTimer = useCallback(() => {
    if (errorTimer.current) {
      clearTimeout(errorTimer.current);
      errorTimer.current = null;
    }
  }, []);

  const clearSynthTimer = useCallback(() => {
    if (synthTimer.current) {
      clearTimeout(synthTimer.current);
      synthTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      audioRef.current?.pause();
      if (errorTimer.current) clearTimeout(errorTimer.current);
      if (synthTimer.current) clearTimeout(synthTimer.current);
      // Revoke only object URLs this instance owns; module-cache entries are shared.
      ownedUrls.current.forEach((url) => URL.revokeObjectURL(url));
      ownedUrls.current.clear();
    };
  }, []);

  const handleClick = useCallback(() => {
    controllerRef.current?.abort();
    clearErrorTimer();
    clearSynthTimer();
    audioRef.current?.pause();

    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;
    let failed = false;
    const fail = () => {
      if (failed || signal.aborted) return;
      failed = true;
      clearSynthTimer();
      setStatus('error');
      clearErrorTimer();
      errorTimer.current = setTimeout(() => setStatus('idle'), 2000);
    };

    const cacheKey = `${language}|${word}`;

    const startPlayback = (objectUrl: string) => {
      if (signal.aborted) return;
      const audio = new Audio();
      audio.preload = 'auto';
      audioRef.current = audio;

      const settle = () => {
        clearSynthTimer();
        setStatus('idle');
      };
      audio.addEventListener('playing', settle, { signal });
      audio.addEventListener('ended', settle, { signal });
      audio.addEventListener('error', fail, { signal });
      audio.addEventListener('abort', fail, { signal });

      // Start playback only once the element has buffered enough to play through.
      // Calling play() immediately after createObjectURL() races the decode: the
      // browser fires `playing` and advances currentTime while still buffering,
      // so a short clip can finish before any audio is output (silent first
      // play, works on the second tap once the blob is warm). `canplaythrough`
      // is the readiness signal; a fallback timer covers browsers that are slow
      // to emit it, and `load()` kicks off buffering.
      let started = false;
      const startOnce = () => {
        if (started || signal.aborted) return;
        started = true;
        void audio.play().catch(() => fail());
      };
      audio.addEventListener('canplaythrough', startOnce, { signal });
      const readyFallback = setTimeout(startOnce, 1200);
      signal.addEventListener('abort', () => clearTimeout(readyFallback));

      audio.src = objectUrl;
      audio.load();
    };

    setStatus('loading');
    // Timeout guard: a slow first-time TTS synth (no axios timeout is configured)
    // must not leave the spinner hung forever.
    synthTimer.current = setTimeout(fail, SYNTH_TIMEOUT_MS);

    const cached = audioUrlCache.get(cacheKey);
    if (cached) {
      startPlayback(cached);
      return;
    }

    fetchVocabAudioBlob(word, language)
      .then((blob) => {
        if (signal.aborted) return;
        const objectUrl = URL.createObjectURL(blob);
        audioUrlCache.set(cacheKey, objectUrl);
        startPlayback(objectUrl);
      })
      .catch(() => fail());
  }, [word, language, clearErrorTimer, clearSynthTimer]);

  return (
    <IconButton
      ref={buttonRef}
      onClick={handleClick}
      disabled={status === 'error'}
      aria-label={t('reviewPage.playPronunciation', 'Play pronunciation')}
      sx={sx}
    >
      {status === 'loading' ? (
        <CircularProgress size={20} color="inherit" />
      ) : status === 'error' ? (
        <VolumeOffIcon />
      ) : (
        <VolumeUpIcon />
      )}
    </IconButton>
  );
});
